import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { WalletProvider } from "@/context/WalletContext";
import { DeploymentProvider } from "@/context/DeploymentContext";
import { LiveProvider } from "@/context/LiveContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import Header from "@/components/Header";
import Board from "@/pages/Board";
import CoinPage from "@/pages/CoinPage";
import Portfolio from "@/pages/Portfolio";
import Profile from "@/pages/Profile";
import HowItWorks from "@/pages/HowItWorks";
import Admin from "@/pages/Admin";
import Vesting from "@/pages/Vesting";
import Whitepaper from "@/pages/Whitepaper";
import Points from "@/pages/Points";
import CreateCoinModal from "@/components/CreateCoinModal";
import Footer from "@/components/Footer";
import ReferralBindBanner from "@/components/ReferralBindBanner";

// All pages are eagerly bundled so navigation NEVER triggers a per-click chunk download —
// mobile in-wallet browsers (Slush) have unreliable JS caching, so on-demand chunks made
// every page tap wait for a network fetch. One initial download (masked by the HTML loading
// shell) then instant navigation. The heavy Cetus SDK stays lazy-imported inside lib/cetus.js.

function Shell() {
  const [createOpen, setCreateOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const location = useLocation();
  const { theme } = useTheme();

  // Capture a referral code (?ref=) so it binds to the wallet once connected.
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (ref) localStorage.setItem("suipump_ref", ref);
  }, [location.search]);

  return (
    <>
      <div className="aurora-bg" aria-hidden="true" />
      <div className="grid-overlay" aria-hidden="true" />
      <Header onCreate={() => setCreateOpen(true)} />
      <ReferralBindBanner />
      <Routes>
        <Route path="/" element={<Board key={refreshKey} onCreate={() => setCreateOpen(true)} />} />
        <Route path="/coin/:id" element={<CoinPage />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/u/:handle" element={<Profile />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/vesting" element={<Vesting />} />
        <Route path="/whitepaper" element={<Whitepaper />} />
        <Route path="/airdrop" element={<Points />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
      {createOpen && (
        <CreateCoinModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => setRefreshKey((k) => k + 1)} />
      )}
      <Footer />
      <Toaster theme={theme} position="top-center" offset={72} duration={3500} toastOptions={{ style: { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", color: "hsl(var(--foreground))" } }} />
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <WalletProvider>
        <DeploymentProvider>
          <LiveProvider>
            <BrowserRouter>
              <Shell />
            </BrowserRouter>
          </LiveProvider>
        </DeploymentProvider>
      </WalletProvider>
    </ThemeProvider>
  );
}
